import React, { createContext, useContext, useState, useRef, useCallback } from "react";
import { postsAPI } from "../services/api";

// Split context into State (volatile) and Dispatch/Actions (stable)
const PostUploadStateContext = createContext(null);
const PostUploadActionsContext = createContext(null);

export const usePostUploadState = () => {
    const context = useContext(PostUploadStateContext);
    if (!context) {
        throw new Error("usePostUploadState must be used within a PostUploadProvider");
    }
    return context;
};

export const usePostUploadActions = () => {
    const context = useContext(PostUploadActionsContext);
    if (!context) {
        throw new Error("usePostUploadActions must be used within a PostUploadProvider");
    }
    return context;
};

// Backward compatibility hook (returns both) - Use with caution as it causes re-renders on state change
export const usePostUpload = () => {
    const state = usePostUploadState();
    const actions = usePostUploadActions();
    return { ...state, ...actions };
};

export const PostUploadProvider = ({ children }) => {
    const [uploads, setUploads] = useState({}); // { [id]: { id, status, progress, error, postData } }
    const uploadsRef = useRef({});

    // Helper to safely update uploads state
    const updateUploadState = useCallback((id, updates) => {
        setUploads((prev) => {
            const newState = {
                ...prev,
                [id]: { ...prev[id], ...updates },
            };
            uploadsRef.current = newState;
            return newState;
        });
    }, []);

    const startUpload = useCallback(async (postData) => {
        const uploadId = Date.now().toString();

        // Initial state
        const newUpload = {
            id: uploadId,
            status: "uploading",
            progress: 0,
            error: null,
            postData: postData, // Store strictly for retry/debug if needed
            startTime: Date.now(),
        };

        updateUploadState(uploadId, newUpload);

        try {
            // Call API
            // Note: postData can be FormData or a regular object
            const response = await postsAPI.createPost(postData, (progress) => {
                // This runs frequently (e.g. 50ms). 
                // State update here triggers re-render of anything consuming PostUploadStateContext.
                updateUploadState(uploadId, { progress });
            });

            // Success
            updateUploadState(uploadId, {
                status: "completed",
                progress: 100,
                response: response.data,
                endTime: Date.now()
            });

            // Dispatch global event so components can update their feed
            // Check multiple success formats
            const isSuccess = response.data && (
                response.data.success === true || 
                response.data.status === 'success' ||
                response.status === 200 ||
                response.status === 201
            );
            
            // Get post data from various possible response formats
            const responsePostData = response.data?.data || response.data?.post || response.data;
            
            if (isSuccess && responsePostData) {
                const postEvent = new CustomEvent('post-upload-completed', {
                    detail: {
                        post: responsePostData,
                        uploadId: uploadId
                    }
                });
                window.dispatchEvent(postEvent);
            }

            // Remove from list after delay
            setTimeout(() => {
                setUploads((prev) => {
                    const newState = { ...prev };
                    delete newState[uploadId];
                    uploadsRef.current = newState;
                    return newState;
                });
            }, 5000); // Keep success message for 5 seconds

            return response.data;

        } catch (error) {
            console.error("Background upload failed:", error);
            updateUploadState(uploadId, {
                status: "error",
                error: error.message || "Upload failed"
            });
            throw error;
        }
    }, [updateUploadState]);

    const startUpdate = useCallback(async (postId, postData) => {
        const uploadId = `update-${postId}-${Date.now()}`;

        // Initial state
        const newUpload = {
            id: uploadId,
            status: "uploading",
            progress: 0,
            error: null,
            postData: postData,
            postId: postId,
            startTime: Date.now(),
        };

        updateUploadState(uploadId, newUpload);

        try {
            // Call API
            const response = await postsAPI.updatePost(postId, postData, (progress) => {
                updateUploadState(uploadId, { progress });
            });

            // Success
            updateUploadState(uploadId, {
                status: "completed",
                progress: 100,
                response: response.data,
                endTime: Date.now()
            });

            // Dispatch global event so components can update their feed
            const isSuccess =
                response.data &&
                (response.data.success === true ||
                    response.data.status === 'success' ||
                    response.status === 200);

            // Try multiple common shapes for updated post payloads
            const responsePostData =
                response.data?.data?.post ||
                response.data?.post ||
                response.data?.data ||
                response.data;

            if (isSuccess && responsePostData) {
                const postEvent = new CustomEvent('post-update-completed', {
                    detail: {
                        post: responsePostData,
                        uploadId: uploadId,
                        postId: postId,
                        postData: postData, // include submitted data for optimistic merge fallbacks
                    }
                });
                window.dispatchEvent(postEvent);
            }

            // Remove from list after delay
            setTimeout(() => {
                setUploads((prev) => {
                    const newState = { ...prev };
                    delete newState[uploadId];
                    uploadsRef.current = newState;
                    return newState;
                });
            }, 5000);

            return response.data;

        } catch (error) {
            console.error("Background update failed:", error);
            updateUploadState(uploadId, {
                status: "error",
                error: error.message || "Update failed"
            });
            throw error;
        }
    }, [updateUploadState]);

    const removeUpload = useCallback((id) => {
        setUploads((prev) => {
            const newState = { ...prev };
            delete newState[id];
            uploadsRef.current = newState;
            return newState;
        });
    }, []);

    const retryUpload = useCallback((id) => {
        const upload = uploadsRef.current[id];
        if (upload && upload.status === "error") {
            // Remove failed state and start over
            removeUpload(id); // Clean up old one FIRST

            // We need to use valid postData.
            if (upload.postData) {
                startUpload(upload.postData);
            }
        }
    }, [removeUpload, startUpload]);

    return (
        <PostUploadActionsContext.Provider value={{ startUpload, startUpdate, removeUpload, retryUpload }}>
            <PostUploadStateContext.Provider value={{ uploads }}>
                {children}
            </PostUploadStateContext.Provider>
        </PostUploadActionsContext.Provider>
    );
};
